import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';

import {
  adminCreateFlexiblePlan,
  adminUpdateFlexiblePlan,
  IFlexiblePlan,
  TAdminCreateFlexiblePlan,
  TAdminUpdateFlexiblePlan,
} from '../../../../../../store/V2/FlexiblePlansSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import {
  DarkButton,
  DarkLoadingButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import {
  TFlexiblePlanExpiredInTypeOption,
  TFlexiblePlanStatusOption,
  flexiblePlanExpiredInTypeOptions,
  flexiblePlanStatusOptions,
} from '../../../../../../constants';
import { capitalizeFirstLetter } from '../../../../../../helper';

interface AddFlexiblePlanModalProps {
  open: boolean;
  onClose: () => void;
  flexiblePlan?: IFlexiblePlan;
  readOnly?: boolean;
  duplicate?: boolean;
}

type TState = {
  name: string;
  status: TFlexiblePlanStatusOption;
  expiredInCount: string;
  expiredInType: TFlexiblePlanExpiredInTypeOption;
  numberOfCredits: string;
  planFeatureText: string;
  features: string[];
  isMostPopular: boolean;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultFeatures = [
  'Lesson Recordings',
  'Progress Tracker',
  'Learning Platform Access',
  'Feedback from Tutor',
  'Chat with Tutor',
];

const initialState: TState = {
  name: '',
  status: flexiblePlanStatusOptions[0],
  expiredInCount: '1',
  expiredInType: flexiblePlanExpiredInTypeOptions[3],
  numberOfCredits: '',
  planFeatureText: '',
  features: [...defaultFeatures],
  isMostPopular: false,
};

const AddFlexiblePlanModal: FunctionComponent<AddFlexiblePlanModalProps> = ({
  open,
  onClose,
  flexiblePlan,
  readOnly = false,
  duplicate = false,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  let mode: 'edit' | 'new' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Flexible Plan';

  const isSubmitDisabled =
    !state.name.trim().length ||
    !state.status ||
    !state.expiredInCount.trim().length ||
    (state.expiredInCount && parseFloat(state.expiredInCount) < 1) ||
    !state.expiredInType ||
    !state.features.length;

  if (flexiblePlan) {
    mode = 'edit';
    modalTitle = 'Edit Flexible Plan';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Flexible Plan Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Flexible Plan';
    }
  }

  useEffect(() => {
    if (mode !== 'new' && flexiblePlan) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (flexiblePlan.name) {
          newStateData.name = flexiblePlan.name;
        }

        if (flexiblePlan.isMostPopular) {
          newStateData.isMostPopular = flexiblePlan.isMostPopular;
        }

        if (flexiblePlan.numberOfCredits) {
          newStateData.numberOfCredits = (
            flexiblePlan.numberOfCredits / 60
          ).toString();
        }

        if (flexiblePlan.expiredIn) {
          newStateData.expiredInCount = flexiblePlan.expiredIn.count.toString();

          const matchingExpiredInType = flexiblePlanExpiredInTypeOptions.find(
            (item) => item.value === flexiblePlan.expiredIn.type
          );

          if (matchingExpiredInType) {
            newStateData.expiredInType = matchingExpiredInType;
          }
        }

        if (flexiblePlan.status) {
          const matchingStatus = flexiblePlanStatusOptions.find(
            (item) => item.value === flexiblePlan.status
          );

          if (matchingStatus) {
            newStateData.status = matchingStatus;
          }
        }

        if (flexiblePlan.features.length) {
          newStateData.features = flexiblePlan.features;
        }

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleAddFeature = () => {
    setState((prevState) => {
      const newStateData = { ...prevState };
      const newFeatures = [...prevState.features];

      if (!newFeatures.includes(state.planFeatureText)) {
        newFeatures.push(state.planFeatureText);
        newStateData.planFeatureText = '';
      }

      newStateData.features = newFeatures;

      return newStateData;
    });
  };

  const handleRemoveFeature = (featureItem: string) => {
    setState((prevState) => {
      const newStateData = {
        ...prevState,
        features: [...prevState.features].filter(
          (item) => item !== featureItem
        ),
      };

      return newStateData;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateFlexiblePlan = {
        name: state.name,
        status: state.status.value,
        features: state.features,
        isMostPopular: state.isMostPopular,
        expiredIn: {
          count: parseInt(state.expiredInCount, 10),
          type: state.expiredInType.value,
        },
      };

      if (parseInt(state.numberOfCredits, 10) > 0) {
        apiData.numberOfCredits = parseInt(state.numberOfCredits, 10) * 60;
      }

      const res: any = await dispatch(adminCreateFlexiblePlan(apiData));

      if (res.payload) {
        onClose();
      }
    } else if (mode === 'edit' && flexiblePlan) {
      const apiData: TAdminUpdateFlexiblePlan = {
        id: flexiblePlan._id,
        name: state.name,
        status: state.status.value,
        features: state.features,
        isMostPopular: state.isMostPopular,
        expiredIn: {
          count: parseInt(state.expiredInCount, 10),
          type: state.expiredInType.value,
        },
      };

      if (parseInt(state.numberOfCredits, 10) > 0) {
        apiData.numberOfCredits = parseInt(state.numberOfCredits, 10) * 60;
      }

      const res: any = await dispatch(adminUpdateFlexiblePlan(apiData));

      if (res.payload) {
        onClose();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Name *"
                  placeholder="Enter here..."
                  value={state.name}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  type="number"
                  label="Number of credits (Hours)"
                  placeholder="Enter here..."
                  value={state.numberOfCredits}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({
                      numberOfCredits: event.target.value,
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  disableClearable
                  fullWidth
                  size="small"
                  readOnly={readOnly}
                  value={state.status}
                  options={flexiblePlanStatusOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(event, value) =>
                    updateStateAttributes({ status: value })
                  }
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label)
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Status *"
                      placeholder="Status"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                disabled={readOnly}
                control={<Checkbox checked={state.isMostPopular} />}
                label="Most Popular Plan"
                onChange={(event, value) =>
                  updateStateAttributes({ isMostPopular: value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight={600}>
                Expired In *
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  type="number"
                  placeholder="Enter here..."
                  value={state.expiredInCount}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({
                      expiredInCount: event.target.value,
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <Autocomplete
                  disableClearable
                  fullWidth
                  size="small"
                  readOnly={readOnly}
                  value={state.expiredInType}
                  options={flexiblePlanExpiredInTypeOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(event, value) =>
                    updateStateAttributes({ expiredInType: value })
                  }
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label.toLowerCase())
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Type"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight={600}>
                Features *
              </Typography>
            </Grid>
            {mode !== 'view' ? (
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" gap="10px">
                  <TPTextField
                    fullWidth
                    {...textFieldProps}
                    label="Plan Feature *"
                    placeholder="Enter here..."
                    value={state.planFeatureText}
                    onChange={(event) =>
                      updateStateAttributes({
                        planFeatureText: event.target.value,
                      })
                    }
                  />
                  <DarkButton
                    variant="contained"
                    color="inherit"
                    sx={{ ...defaultButtonStyles }}
                    onClick={handleAddFeature}
                    disabled={!state.planFeatureText.trim().length}
                  >
                    Add
                  </DarkButton>
                </Stack>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Stack gap="10px">
                {state.features.map((item, index) => {
                  return (
                    <Stack
                      key={`feature-${index}`}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography fontSize="14px" fontWeight={500}>
                        {item}
                      </Typography>
                      {mode !== 'view' ? (
                        <IconButton onClick={() => handleRemoveFeature(item)}>
                          <DeleteIcon sx={{ color: '#1E293B' }} />
                        </IconButton>
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            </Grid>
            {!state.features.length ? (
              <Grid item xs={12}>
                <Typography fontSize="14px" fontWeight={500}>
                  No features are available
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AddFlexiblePlanModal;
